<template>
  <AppModal
    v-model="isOpen"
    panel-class="tw-max-w-5xl tw-h-[90vh]"
    @close="closeModal"
  >
    <template #header>
      <div class="tw-relative tw-mb-10 tw-flex tw-items-center tw-justify-center">
        <button
          v-if="!isExit"
          type="button"
          class="tw-absolute tw-left-0 tw-flex tw-items-center tw-text-dark"
          aria-label="Back"
          @click="getPreviousFollowUpQuestion"
        >
          <IconBackArrow />
        </button>
        <img
          src="@/assets/images/logo_black.svg"
          alt="Winona"
          class="tw-h-5"
        >
      </div>
    </template>

    <div class="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-px-2">
      <FollowupV7Single
        v-if="followUpType === 'single'"
        :question="displayFollowup"
        :disabled="buttonDisabled"
        @submitAnswer="answerFollowUpQuestion"
      />
      <FollowupV7Text
        v-else-if="followUpType === 'text'"
        :question="displayFollowup"
        :disabled="buttonDisabled"
        @submitAnswer="answerFollowUpQuestion"
        @alert="onAlert"
      />
      <FollowupV7Exit
        v-else-if="followUpType === 'nothing'"
        :question="displayFollowup"
        :disabled="buttonDisabled"
        @submitAnswer="answerFollowUpQuestion"
      />

      <div
        v-if="composite && secondQuestionBody.answertype === 'text'"
        class="tw-mt-8 tw-w-full"
      >
        <FollowupV7Text
          :question="secondQuestionBody"
          :disabled="buttonDisabled"
          composite
          @submitAnswer="answerFollowUpQuestion"
          @alert="onAlert"
        />
      </div>
    </div>

    <template #footer>
      <p class="tw-mt-10 tw-text-center tw-font-atten-bold tw-text-sm tw-text-dark">
        Treatment Review
      </p>
    </template>
  </AppModal>
</template>

<script>
import { mapGetters } from 'vuex';

import AppModal from '@/components/app/Modal.vue';
import FollowupV7Single from '@/components/followup/v7/single.vue';
import FollowupV7Text from '@/components/followup/v7/text.vue';
import FollowupV7Exit from '@/components/followup/v7/exit.vue';
import IconBackArrow from '@/components/icon/IconBackArrow.vue';
import { ChatService } from '@/services/chat.service.js';

/**
 * FollowupQuestionVersion v7 modal.
 *
 * A backend-driven question flow (single / text / nothing answer types) walked
 * via `next` pointers. Data access uses `ChatService` directly. An optional
 * `provider` prop (same interface as `ChatService`) overrides it so the modal
 * can be exercised offline with `createStubProvider` — see Playground.vue.
 *
 * Public API (call via a template ref):
 *   - await setFollowUpMessage(messageId, step)  // load a question
 *   - show()                                     // reveal the modal
 *   - hide()                                     // dismiss the modal
 */
export default {
  name: 'FollowupV7',

  components: {
    AppModal,
    FollowupV7Single,
    FollowupV7Text,
    FollowupV7Exit,
    IconBackArrow,
  },

  props: {
    messages: {
      type: Array,
      default: () => [],
    },
    provider: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      isOpen: false,
      followupList: [],
      followUpID: '',
      followUpAnswers: [],
      composite: false,
      firstAnswer: null,
      secondQuestionBody: {},
      previousFollowUpID: '',
      currentFollowupStep: null,
      buttonDisabled: false,
    };
  },

  computed: {
    ...mapGetters({
      user: 'user/getUser',
    }),
    service() {
      return this.provider || ChatService;
    },
    followup() {
      return this.followupList.length > 0 ? this.followupList[0] : {};
    },
    followUpType() {
      return this.followup.answertype || '';
    },
    isExit() {
      return this.followUpType === 'nothing';
    },
    firstName() {
      return (this.user && (this.user.firstname || this.user.firstName)) || '';
    },
    // Resolve `@firstname` tokens before handing the question to children.
    displayFollowup() {
      const question = this.followup;

      if (!question || !question.title) {
        return question;
      }

      return {
        ...question,
        title: question.title.replace('@firstname', this.firstName),
      };
    },
  },

  methods: {
    // eslint-disable-next-line vue/no-unused-properties
    show() {
      this.isOpen = true;
    },
    hide() {
      this.isOpen = false;
    },
    closeModal() {
      this.hide();
      this.$emit('hide');
    },
    onAlert(message) {
      this.$emit('alert', message);
    },

    async setFollowUpMessage(messageid, step = 1) {
      this.composite = false;
      this.followUpID = messageid;
      this.secondQuestionBody = {};
      try {
        this.buttonDisabled = true;
        const followup = await this.service.getFollowUpQuestion(messageid, step);
        this.buttonDisabled = false;

        if (Array.isArray(followup.data) && followup.data.length > 0) {
          const fmessage = this.messages.find(x => x.id === messageid);
          this.followupList = followup.data;
          if (fmessage) this.followupList.actionstatus = fmessage.actionstatus;
          return { ok: true };
        }

        return {
          ok: false,
          message: typeof followup.data === 'string' ? followup.data : null,
        };
      } catch (e) {
        console.error(e);
        this.buttonDisabled = false;
        return {
          ok: false,
          message:
            e.response?.data?.message ||
            (typeof e.response?.data === 'string' ? e.response.data : null),
        };
      }
    },

    async answerFollowUpQuestion(id, answer, answerBody = null, moreQuestions) {
      try {
        const messageid = this.followUpID;

        // Overwrite any previous answer for this question id.
        this.followUpAnswers = this.followUpAnswers.filter(o => o.questionid !== id);
        if (id === this.followup?.id) {
          this.firstAnswer = answer;
        }

        if (answerBody && answerBody.inline) {
          // Reveal a composite (inline) follow-up beneath the current choice.
          this.followUpAnswers = [];
          this.composite = true;
          this.previousFollowUpID = id;
          const compositeReq = await this.service.getFollowUpQuestion(
            messageid,
            answerBody.next
          );
          this.secondQuestionBody = compositeReq.data[0];
          this.followUpAnswers.push({ questionid: id, answer });
          return;
        }

        this.followUpAnswers.push({
          questionid: id,
          answer,
          ...(moreQuestions && { others: [moreQuestions] }),
        });

        // Composite submits use the inline parent question as the base id.
        const baseId =
          this.followUpAnswers.length > 1 ? this.previousFollowUpID : id;
        this.buttonDisabled = true;

        const res = await this.service.answerFollowUpQuestion(messageid, baseId, {
          answers: this.followUpAnswers,
        });

        this.followUpAnswers = [];
        if (res.data.next === -1) {
          await this.handleFollowupComplete();
          return;
        }

        await this.setFollowUpMessage(this.followUpID, res.data.next);
        this.currentFollowupStep = res.data.next;
        this.buttonDisabled = false;
      } catch (error) {
        this.buttonDisabled = false;
        console.error(error);
      }
    },

    async getPreviousFollowUpQuestion() {
      if (this.followup?.id === 1) {
        this.closeModal();
        return;
      }
      this.composite = false;
      this.followUpAnswers = [];
      this.secondQuestionBody = {};
      const res = await this.service.getPreviousFollowUpQuestion(
        this.followUpID,
        this.followup?.id
      );
      if (res.data && res.data.length > 0) {
        const parent = res.data[0];
        this.currentFollowupStep = parent.id;
        this.followupList = res.data;

        /*
          Only re-show the inline composite text field when the restored answer
          maps to an inline option (matches the forward-path gate), not merely
          because the /previous endpoint returned more than one record.
        */
        const savedEntry = (parent.answerlist || []).find(
          option => String(option.id) === String(parent.answer)
        );

        if (res.data.length > 1 && savedEntry && savedEntry.inline) {
          this.composite = true;
          this.firstAnswer = parent.answer;
          this.secondQuestionBody = res.data[1];
        }
      }
    },

    async handleFollowupComplete() {
      this.$emit('complete');
      this.closeModal();
    },
  },
};
</script>
